import cookies from 'js-cookie'
import { $http, urlParams } from '@/munio/http'
import Storage from '@/munio/storage.js'
import { safeRedirect } from '@/munio/utils/url'

/**
 * Global handler for the data object from JSON responses.
 */
export function handleJSONResponseData(data: any): boolean {
  if (!data) {
    return true
  }

  if (Array.isArray(data.toasts)) {
    data.toasts.forEach((toast: any) => window.Munio.Notifications.show(toast))
  }

  if (data.redirect) {
    const redirect = safeRedirect(data.redirect)

    if (!redirect) {
      // Refuse the navigation outright. Reloading here would loop forever for any
      // automatically fired request whose response carries a rejected target.
      console.error('Blocked unsafe redirect target', data.redirect)

      return true
    }

    if (window.location.href === redirect) {
      window.location.reload()
    } else {
      window.location.href = redirect
    }

    return false
  }

  if (data.refresh) {
    window.location.reload()
  }

  return true
}

/**
 * Output CSRF token
 */
export function csrf_token(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute('content') ?? ''
  )
}

/**
 * Output encrypted CSRF token
 */
export function xsrf_token(): string {
  return cookies.get('XSRF-TOKEN') ?? ''
}

export function getDispositionFilename(
  disposition: string,
  fallback: string = '',
): string {
  disposition = disposition + ''
  const matches = disposition.match(/filename[^;=\n]*=(UTF-8(['"]*))?(.*)/)

  if (matches !== null && matches[3]) {
    return decodeURI(matches[3].replaceAll(/['"]/g, ''))
  }

  return fallback
}

export function downloadFile(
  data: string,
  filename: string,
  mime: string,
  bom?: string,
) {
  // @author https://github.com/kennethjiang/js-file-download/blob/master/file-download.js
  const url = window.URL || window.webkitURL
  const blobData: BlobPart[] = typeof bom !== 'undefined' ? [bom, data] : [data]
  const blob = new Blob(blobData, { type: mime || 'application/octet-stream' })

  const nav = window.navigator as Navigator & {
    msSaveBlob?: (blob: Blob, filename: string) => boolean
  }

  if (typeof nav.msSaveBlob !== 'undefined') {
    nav.msSaveBlob(blob, filename)
  } else {
    const blobURL = url.createObjectURL(blob)
    const tempLink = document.createElement('a')
    tempLink.style.display = 'none'
    tempLink.href = blobURL
    tempLink.setAttribute('download', filename)

    if (typeof tempLink.download === 'undefined') {
      tempLink.setAttribute('target', '_blank')
    }

    document.body.appendChild(tempLink)
    tempLink.click()
    document.body.removeChild(tempLink)
    url.revokeObjectURL(blobURL)
  }
}

interface CsvOptions {
  header?: boolean
  delimiter?: string
  id?: string
}

export async function downloadCSV(
  baseUrl: string,
  options: CsvOptions = {},
  params: string = window.location.search,
) {
  const blockNavigate = function (e: BeforeUnloadEvent) {
    // Cancel the event
    e.preventDefault() // If you prevent default behavior in Mozilla Firefox prompt will always be shown
    // Chrome requires returnValue to be set
    e.returnValue = ''
    return 'Download in progress. Are you sure you want to leave?'
  }

  const settings = Storage.getWithDefault(
    'csvSettings',
    window.Munio.config.csv,
  )

  if (typeof options.header === 'boolean') {
    settings.header = options.header
  }
  if (typeof options.delimiter === 'string') {
    settings.delimiter = options.delimiter
  }
  Storage.set('csvSettings', settings)

  const parameters = urlParams(params)
  parameters.set('export', 'csv')
  parameters.set('csv[header]', settings.header)
  parameters.set('csv[delimiter]', settings.delimiter)
  if (options.id) {
    parameters.set('csv[id]', options.id)
  }

  window.addEventListener('beforeunload', blockNavigate)

  try {
    const { data, headers } = await $http.get<string>(
      baseUrl,
      parameters,
      {},
      { responseType: 'blob' },
    )
    const _mime = headers['content-type'] || 'text/csv;charset=utf-8'
    const _filename = getDispositionFilename(headers['content-disposition'])

    downloadFile(data, _filename, _mime)
  } catch (err) {
    console.error(err)
    Munio.Flash.error(err instanceof Error ? err.message : String(err))
  } finally {
    window.removeEventListener('beforeunload', blockNavigate)
  }
}
